<template>
  <div class="ai-knowledge">
    <div class="ai-stats-grid">
      <article class="ai-stat-card ai-stat-card--ready">
        <span>Prêts</span>
        <strong>{{ readyCount }}</strong>
      </article>
      <article class="ai-stat-card ai-stat-card--processing">
        <span>En traitement</span>
        <strong>{{ processingCount }}</strong>
      </article>
      <article class="ai-stat-card ai-stat-card--warning">
        <span>À réindexer</span>
        <strong>{{ warningCount }}</strong>
      </article>
      <article class="ai-stat-card ai-stat-card--failed">
        <span>Échecs</span>
        <strong>{{ failedCount }}</strong>
      </article>
    </div>

    <section class="ai-table-card">
      <header class="ai-table-header">
        <div>
          <h4>Inventaire documentaire</h4>
          <p>{{ documents.length }} document(s) disponibles pour ce périmètre.</p>
        </div>
        <div class="ai-table-header-actions">
          <p v-if="loading" class="ai-loading-copy">Chargement...</p>
          <button type="button" class="ai-primary-action" @click="openUpload">
            <i class="fas fa-plus-circle" aria-hidden="true"></i>
            <span>Ajouter un document</span>
          </button>
          <button
            type="button"
            class="ai-refresh-action"
            :disabled="loading"
            title="Actualiser les statuts"
            aria-label="Actualiser les statuts des documents"
            @click="loadDocuments"
          >
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }" aria-hidden="true"></i>
          </button>
        </div>
      </header>

      <div class="ai-table-body">
        <b-table
          :data="documents"
          :loading="loading"
          hoverable
          default-sort="created_at"
          default-sort-direction="desc"
          :paginated="documents.length > 15"
          per-page="15"
        >
          <b-table-column field="title" label="Titre" sortable searchable v-slot="props">
            <div class="ai-document-title">
              <span>{{ props.row.title }}</span>
              <small v-if="!props.row.domain_id">Global</small>
            </div>
          </b-table-column>

          <b-table-column field="domain_label" label="Domaine" sortable searchable v-slot="props">
            <span class="ai-domain-name">{{ props.row.domain_label }}</span>
          </b-table-column>

          <b-table-column
            field="doc_type_label"
            label="Type"
            sortable
            searchable
            :custom-search="docTypeSearch"
          >
            <template #default="props">
              <span class="ai-type-pill">{{ docTypeLabel(props.row.doc_type) }}</span>
            </template>
            <template #searchable="{ column, filters }">
              <b-select size="is-small" v-model="filters[column.field]">
                <option value="">Tous</option>
                <option
                  v-for="option in docTypeFilterOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </b-select>
            </template>
          </b-table-column>

          <b-table-column field="chunks_count" label="Fragments" numeric sortable v-slot="props">
            <span class="ai-fragment-count">{{ props.row.chunks_count }}</span>
          </b-table-column>

          <b-table-column field="status" label="Statut" v-slot="props">
            <div class="ai-status-cell" :title="props.row.indexing_issue || ''">
              <span class="ai-status-pill" :class="`ai-status-pill--${statusTone(props.row)}`">
                {{ statusLabel(props.row) }}
              </span>
              <i v-if="props.row.indexing_issue" class="fas fa-info-circle ai-status-info" aria-hidden="true"></i>
            </div>
          </b-table-column>

          <b-table-column field="created_at" label="Ajouté le" sortable v-slot="props">
            {{ formatDate(props.row.created_at) }}
          </b-table-column>

          <b-table-column label="Actions" width="270" v-slot="props">
            <div class="ai-document-actions">
              <button
                class="ai-icon-action"
                :class="{ 'is-disabled': !props.row.can_manage || isDocumentBusy(props.row) }"
                :disabled="!props.row.can_manage || isDocumentBusy(props.row)"
                :title="editActionTitle(props.row)"
                @click="openDocumentEditor(props.row)"
              >
                <i class="fas fa-edit" aria-hidden="true"></i>
              </button>
              <button
                class="ai-icon-action"
                :class="{ 'is-disabled': !props.row.has_original_file }"
                :disabled="!props.row.has_original_file"
                title="Télécharger le document original"
                @click="downloadOriginal(props.row)"
              >
                <i class="fas fa-download" aria-hidden="true"></i>
              </button>
              <button
                class="ai-icon-action"
                :class="{ 'is-disabled': !props.row.can_reindex }"
                :disabled="!props.row.can_reindex || isIndexingActionPending(props.row)"
                :title="reindexActionTitle(props.row)"
                :aria-label="reindexActionTitle(props.row)"
                @click="confirmReindex(props.row)"
              >
                <i class="fas fa-redo" :class="{ 'fa-spin': isReindexing(props.row) }" aria-hidden="true"></i>
              </button>
              <button
                class="ai-icon-action ai-icon-action--warning"
                :class="{ 'is-disabled': !props.row.has_original_file }"
                :disabled="!props.row.can_reextract || isIndexingActionPending(props.row)"
                :title="reextractActionTitle(props.row)"
                :aria-label="reextractActionTitle(props.row)"
                @click="confirmReextract(props.row)"
              >
                <i class="fas fa-file-import" :class="{ 'fa-spin': isReextracting(props.row) }" aria-hidden="true"></i>
              </button>
              <button
                class="ai-icon-danger"
                :disabled="!props.row.can_manage || isDocumentBusy(props.row)"
                :title="deleteActionTitle(props.row)"
                @click="confirmDelete(props.row)"
              >
                <i class="fas fa-trash" aria-hidden="true"></i>
              </button>
            </div>
          </b-table-column>

          <template #empty>
            <div class="ai-empty-table">
              Aucun document pour ce domaine.
            </div>
          </template>
        </b-table>
      </div>
    </section>
  </div>
</template>

<script>
import { format } from 'date-fns'
import aiService from '@/services/ai.service'
import UploadDocument from './Modals/UploadDocument.vue'
import EditDocument from './Modals/EditDocument.vue'

const DOC_TYPE_LABELS = {
  accord: "Accord d'entreprise",
  avenant: 'Avenant',
  ccn: 'Convention collective',
  code_travail: 'Code du travail',
  autre: 'Autre'
}

const DOCUMENT_POLL_INTERVAL = 5000
const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;'
}

function escapeHtml (value) {
  return String(value === undefined || value === null ? '' : value)
    .replace(/[&<>"']/g, character => HTML_ENTITIES[character])
}

export default {
  name: 'app-ai-knowledge',
  props: {
    aiDomain: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      loading: false,
      documents: [],
      reindexingDocumentIds: [],
      reextractingDocumentIds: [],
      documentsRequestSequence: 0,
      documentPollTimer: null,
      pollingEnabled: true
    }
  },
  computed: {
    domain () {
      return this.aiDomain || {}
    },
    readyCount () {
      return this.documents.filter(doc => this.statusTone(doc) === 'ready').length
    },
    processingCount () {
      return this.documents.filter(doc => this.statusTone(doc) === 'processing').length
    },
    warningCount () {
      return this.documents.filter(doc => this.statusTone(doc) === 'warning').length
    },
    failedCount () {
      return this.documents.filter(doc => this.statusTone(doc) === 'failed').length
    },
    docTypeFilterOptions () {
      return Object.keys(DOC_TYPE_LABELS).map(value => ({
        value,
        label: DOC_TYPE_LABELS[value]
      }))
    }
  },
  watch: {
    aiDomain () {
      this.cancelDocumentPolling()
      this.loadDocuments()
    }
  },
  mounted () {
    this.loadDocuments()
  },
  beforeDestroy () {
    this.pollingEnabled = false
    this.documentsRequestSequence += 1
    this.cancelDocumentPolling()
  },
  methods: {
    loadDocuments () {
      return this.fetchDocuments(false)
    },
    fetchDocuments (silent) {
      const requestSequence = ++this.documentsRequestSequence
      const domainUuid = this.domain && this.domain.uuid ? this.domain.uuid : null
      this.cancelDocumentPolling()
      if (!silent) {
        this.loading = true
      }

      return aiService.listDocuments(domainUuid)
        .then(documents => {
          if (requestSequence !== this.documentsRequestSequence) {
            return
          }
          this.documents = Array.isArray(documents) ? documents.map(this.normalizeDocument) : []
        })
        .catch(err => {
          if (requestSequence === this.documentsRequestSequence && !silent) {
            this.notifyLoadError(err)
          }
        })
        .finally(() => {
          if (requestSequence !== this.documentsRequestSequence) {
            return
          }
          this.loading = false
          this.scheduleDocumentPolling()
        })
    },
    cancelDocumentPolling () {
      if (this.documentPollTimer !== null) {
        window.clearTimeout(this.documentPollTimer)
        this.documentPollTimer = null
      }
    },
    scheduleDocumentPolling () {
      this.cancelDocumentPolling()
      if (!this.pollingEnabled || !this.documents.some(document => this.isDocumentBusy(document))) {
        return
      }

      this.documentPollTimer = window.setTimeout(() => {
        this.documentPollTimer = null
        this.fetchDocuments(true)
      }, DOCUMENT_POLL_INTERVAL)
    },
    normalizeDocument (document) {
      const domainLabel = document && document.domain && document.domain.label
        ? document.domain.label
        : 'Global'
      const docType = document && document.doc_type ? document.doc_type : 'autre'
      return {
        ...document,
        domain_label: domainLabel,
        doc_type_label: this.docTypeLabel(docType)
      }
    },
    openUpload () {
      this.$buefy.modal.open({
        parent: this,
        component: UploadDocument,
        hasModalCard: true,
        trapFocus: true,
        props: {
          domainUuid: this.domain && this.domain.uuid ? this.domain.uuid : '',
          domainLabel: this.domain ? this.domain.label : ''
        },
        events: {
          created: () => this.loadDocuments()
        }
      })
    },
    openDocumentEditor (doc) {
      if (!doc.can_manage || this.isDocumentBusy(doc)) {
        return
      }

      this.$buefy.modal.open({
        parent: this,
        component: EditDocument,
        hasModalCard: true,
        trapFocus: true,
        props: {
          document: doc,
          activeDomain: this.domain
        },
        events: {
          saved: () => this.loadDocuments()
        }
      })
    },
    isReindexing (doc) {
      return !!doc && this.reindexingDocumentIds.includes(doc.id)
    },
    isReextracting (doc) {
      return !!doc && this.reextractingDocumentIds.includes(doc.id)
    },
    isIndexingActionPending (doc) {
      return this.isReindexing(doc) || this.isReextracting(doc)
    },
    documentStatus (doc) {
      if (doc && doc.metadata && doc.metadata.status) {
        return doc.metadata.status
      }
      return doc && doc.status ? doc.status : 'ready'
    },
    isDocumentBusy (doc) {
      const status = this.documentStatus(doc)
      return status === 'queued' || status === 'processing' || this.isIndexingActionPending(doc)
    },
    editActionTitle (doc) {
      if (!doc.can_manage) {
        return 'Ce document global est en lecture seule pour ce domaine'
      }
      return this.isDocumentBusy(doc)
        ? 'Modification indisponible pendant l’indexation'
        : 'Modifier le document'
    },
    reindexActionTitle (doc) {
      if (!doc.can_manage) {
        return 'Ce document global est en lecture seule pour ce domaine'
      }
      if (this.isDocumentBusy(doc)) {
        return 'Indexation déjà en cours'
      }
      if (!doc.can_reindex) {
        return 'Aucun texte sauvegardé n’est disponible pour reconstruire l’index'
      }
      return 'Reconstruire l’index depuis le texte sauvegardé'
    },
    reextractActionTitle (doc) {
      if (!doc.has_original_file) {
        return 'Aucun fichier original disponible'
      }
      if (!doc.can_manage) {
        return 'Ce document global est en lecture seule pour ce domaine'
      }
      if (!doc.can_reextract) {
        return 'Indexation déjà en cours'
      }
      return 'Réextraire le fichier original et remplacer le texte sauvegardé'
    },
    deleteActionTitle (doc) {
      if (!doc.can_manage) {
        return 'Ce document global est en lecture seule pour ce domaine'
      }
      return this.isDocumentBusy(doc)
        ? 'Suppression indisponible pendant l’indexation'
        : 'Supprimer'
    },
    confirmReindex (doc) {
      if (!doc || !doc.can_reindex || this.isIndexingActionPending(doc)) {
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Reconstruire l’index',
        message: `L’index de <b>${this.safeDocumentTitle(doc)}</b> sera reconstruit à partir du texte actuellement sauvegardé. Vos corrections manuelles seront conservées.`,
        confirmText: 'Reconstruire',
        cancelText: 'Annuler',
        onConfirm: () => this.reindexDocument(doc)
      })
    },
    reindexDocument (doc) {
      this.reindexingDocumentIds = [...this.reindexingDocumentIds, doc.id]
      this.resolveContentRevision(doc).then(contentRevision => {
        return aiService.reindexDocument(doc.id, contentRevision)
      }).then(result => {
        const updatedDocument = result && result.document ? result.document : null
        if (updatedDocument) {
          this.documents = this.documents.map(item => item.id === updatedDocument.id ? this.normalizeDocument(updatedDocument) : item)
        } else {
          this.loadDocuments()
        }

        this.notifyIndexingResult(result, 'La reconstruction de l’index a été lancée.')
        this.scheduleDocumentPolling()
      }).catch(err => {
        this.notifyIndexingError(err, 'Impossible de lancer la reconstruction de l’index.')
      }).finally(() => {
        this.reindexingDocumentIds = this.reindexingDocumentIds.filter(id => id !== doc.id)
      })
    },
    confirmReextract (doc) {
      if (!doc || !doc.can_reextract || this.isIndexingActionPending(doc)) {
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Remplacer le texte sauvegardé',
        message: `Le fichier original de <b>${this.safeDocumentTitle(doc)}</b> sera relu. <strong>Toutes les corrections manuelles du texte seront définitivement remplacées</strong>, puis l’index sera reconstruit.`,
        type: 'is-danger',
        hasIcon: true,
        icon: 'exclamation-triangle',
        confirmText: 'Réextraire et remplacer',
        cancelText: 'Annuler',
        onConfirm: () => this.reextractDocument(doc)
      })
    },
    reextractDocument (doc) {
      this.reextractingDocumentIds = [...this.reextractingDocumentIds, doc.id]
      this.resolveContentRevision(doc).then(contentRevision => {
        return aiService.reextractDocument(doc.id, contentRevision)
      }).then(result => {
        const updatedDocument = result && result.document ? result.document : null
        if (updatedDocument) {
          this.documents = this.documents.map(item => item.id === updatedDocument.id ? this.normalizeDocument(updatedDocument) : item)
        } else {
          this.loadDocuments()
        }

        this.notifyIndexingResult(result, 'La réextraction du fichier original a été lancée.')
        this.scheduleDocumentPolling()
      }).catch(err => {
        this.notifyIndexingError(err, 'Impossible de réextraire le fichier original.')
      }).finally(() => {
        this.reextractingDocumentIds = this.reextractingDocumentIds.filter(id => id !== doc.id)
      })
    },
    resolveContentRevision (doc) {
      if (doc.content_revision !== undefined && doc.content_revision !== null) {
        return Promise.resolve(doc.content_revision)
      }

      return aiService.getDocumentText(doc.id).then(result => result.content_revision)
    },
    downloadOriginal (doc) {
      if (!doc.has_original_file) {
        return
      }

      aiService.downloadOriginalDocument(doc.id).then(response => {
        this.downloadBlob(response.data, this.originalFilename(doc, response))
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        this.$buefy.notification.open({
          duration: 4500,
          message: status === 404
            ? 'Le document original est introuvable sur le stockage.'
            : 'Impossible de télécharger le document original.',
          position: 'is-bottom-right',
          type: 'is-danger',
          hasIcon: false
        })
      })
    },
    downloadBlob (blob, filename) {
      const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    originalFilename (doc, response) {
      const disposition = response && response.headers ? response.headers['content-disposition'] : null
      const match = disposition ? disposition.match(/filename="?([^"]+)"?/) : null
      if (match && match[1]) {
        return decodeURIComponent(match[1])
      }

      const extension = doc.metadata && doc.metadata.original_file && doc.metadata.original_file.file_type
        ? `.${doc.metadata.original_file.file_type}`
        : ''
      return `${doc.title || 'document-original'}${extension}`
    },
    confirmDelete (doc) {
      if (!doc.can_manage || this.isDocumentBusy(doc)) {
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Supprimer le document',
        message: `Êtes-vous sûr de vouloir supprimer <b>${this.safeDocumentTitle(doc)}</b> de la base de connaissances ?`,
        type: 'is-danger',
        hasIcon: true,
        icon: 'exclamation-triangle',
        cancelText: 'Annuler',
        confirmText: 'Supprimer',
        onConfirm: () => {
          this.resolveContentRevision(doc).then(contentRevision => {
            return aiService.deleteDocument(doc.id, contentRevision)
          }).then(() => {
            this.documents = this.documents.filter(d => d.id !== doc.id)
            this.$buefy.notification.open({
              duration: 3000,
              message: `<strong>${this.safeDocumentTitle(doc)}</strong> a été supprimé.`,
              position: 'is-bottom-right',
              type: 'is-danger',
              hasIcon: false
            })
          }).catch(err => this.notifyIndexingError(err, 'Impossible de supprimer ce document.'))
        }
      })
    },
    safeDocumentTitle (doc) {
      return escapeHtml(doc && doc.title ? doc.title : '')
    },
    docTypeLabel (type) {
      return DOC_TYPE_LABELS[type] || type
    },
    docTypeSearch (row, input) {
      if (!input) {
        return true
      }

      return row.doc_type === input
    },
    statusLabel (doc) {
      const status = doc.metadata && doc.metadata.status ? doc.metadata.status : 'ready'
      if (status === 'processing' || status === 'queued') {
        return 'En traitement'
      }
      if (status === 'failed') {
        return 'Échec'
      }
      if (doc.indexing_health === 'error') {
        return 'Indexation incomplète'
      }
      if (doc.indexing_health === 'warning') {
        return 'À réindexer'
      }
      if (status === 'ready') {
        return 'Prêt'
      }
      return status
    },
    statusTone (doc) {
      const status = doc.metadata && doc.metadata.status ? doc.metadata.status : 'ready'
      if (status === 'failed') {
        return 'failed'
      }
      if (status === 'processing' || status === 'queued') {
        return 'processing'
      }
      if (doc.indexing_health === 'error') {
        return 'failed'
      }
      if (doc.indexing_health === 'warning') {
        return 'warning'
      }
      return 'ready'
    },
    formatDate (value) {
      if (!value) {
        return ''
      }
      const date = new Date(value)
      return isNaN(date.getTime()) ? '' : format(date, 'dd/MM/yyyy')
    },
    notifyLoadError (err) {
      const status = err && err.response ? err.response.status : null
      this.$buefy.notification.open({
        duration: 4000,
        message: status === 403
          ? 'Vous n\'avez pas le rôle « ai-admin » requis pour gérer la base de connaissances.'
          : 'Impossible de récupérer les documents pour le moment.',
        position: 'is-bottom-left',
        type: 'is-warning',
        hasIcon: false
      })
    },
    notifyIndexingResult (result, fallback) {
      const status = result && result.status
        ? result.status
        : (result && result.document ? this.documentStatus(result.document) : 'ready')
      let type = 'is-info'
      if (status === 'ready') {
        type = 'is-success'
      } else if (status === 'failed') {
        type = 'is-danger'
      }

      this.$buefy.notification.open({
        duration: 5000,
        message: result && result.message ? result.message : fallback,
        position: 'is-bottom-right',
        type,
        hasIcon: false
      })
    },
    notifyIndexingError (err, fallback) {
      const status = err && err.response ? err.response.status : null
      const responseMessage = err && err.response && err.response.data
        ? err.response.data.message
        : null
      let message = responseMessage || fallback

      if (!responseMessage && status === 409) {
        message = 'Le document a changé ou une indexation est déjà en cours. Actualisez la liste avant de recommencer.'
      } else if (!responseMessage && status === 422) {
        message = 'Les informations nécessaires à cette opération sont manquantes.'
      } else if (!responseMessage && status === 403) {
        message = 'Vous n’avez pas le rôle requis pour réindexer ce document.'
      }

      this.$buefy.notification.open({
        duration: 5000,
        message,
        position: 'is-bottom-right',
        type: 'is-danger',
        hasIcon: false
      })
    }
  }
}
</script>

<style scoped>
.ai-knowledge {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ai-table-header h4 {
  margin: 0;
  color: #111827;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.25;
}

.ai-table-header p {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.875rem;
}

.ai-stat-card span {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.1;
}

.ai-primary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 2.65rem;
  border: 1px solid #1d4ed8;
  border-radius: 0.5rem;
  background: #2563eb;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 800;
  padding: 0.6rem 0.9rem;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.ai-primary-action:hover {
  border-color: #1e40af;
  background: #1d4ed8;
}

.ai-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.ai-stat-card {
  min-height: 5.3rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #ffffff;
  padding: 0.85rem;
}

.ai-stat-card strong {
  display: block;
  margin-top: 0.4rem;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
}

.ai-stat-card small {
  display: block;
  margin-top: 0.25rem;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 700;
}

.ai-stat-card--ready {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.ai-stat-card--processing {
  border-color: #fde68a;
  background: #fffbeb;
}

.ai-stat-card--warning {
  border-color: #fed7aa;
  background: #fff7ed;
}

.ai-stat-card--failed {
  border-color: #fecaca;
  background: #fef2f2;
}

.ai-table-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #ffffff;
}

.ai-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem;
}

.ai-loading-copy {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 0.875rem;
}

.ai-table-header-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
}

.ai-refresh-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #475569;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ai-refresh-action:hover:not(:disabled) {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}

.ai-refresh-action:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.ai-table-body {
  padding: 0.75rem;
}

.ai-document-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ai-document-title span {
  color: #111827;
  font-weight: 500;
}

.ai-domain-name {
  color: #111827;
  font-weight: 600;
}

.ai-document-title small,
.ai-type-pill,
.ai-status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1;
  padding: 0.28rem 0.5rem;
}

.ai-document-title small,
.ai-type-pill {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
}

.ai-fragment-count {
  color: #111827;
  font-weight: 800;
}

.ai-status-pill--ready {
  border: 1px solid #86efac;
  background: #dcfce7;
  color: #166534;
}

.ai-status-pill--processing {
  border: 1px solid #fcd34d;
  background: #fef3c7;
  color: #92400e;
}

.ai-status-pill--warning {
  border: 1px solid #fdba74;
  background: #ffedd5;
  color: #9a3412;
}

.ai-status-pill--failed {
  border: 1px solid #fca5a5;
  background: #fee2e2;
  color: #991b1b;
}

.ai-status-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.ai-status-info {
  color: #64748b;
}

.ai-document-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.ai-icon-action,
.ai-icon-danger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  background: transparent;
  color: #475569;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ai-icon-action:hover {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}

.ai-icon-action--warning {
  color: #d97706;
}

.ai-icon-action--warning:hover {
  border-color: #fed7aa;
  background: #fff7ed;
  color: #c2410c;
}

.ai-icon-action.is-disabled,
.ai-icon-action:disabled,
.ai-icon-danger:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

.ai-icon-danger {
  color: #dc2626;
}

.ai-icon-danger:hover {
  border-color: #fecaca;
  background: #fef2f2;
}

.ai-empty-table {
  padding: 2rem 1rem;
  color: #94a3b8;
  text-align: center;
}

@media (max-width: 1024px) {
  .ai-table-header {
    flex-direction: column;
    align-items: stretch;
  }

  .ai-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .ai-primary-action {
    width: 100%;
  }

  .ai-stats-grid {
    grid-template-columns: 1fr;
  }
}
</style>
